import { css, keyframes } from 'styled-components';

import {
  BLOG_WRITE_ANIM,
  BRAND_DEPLOYMENT_GLOW,
  BRAND_DEPLOYMENT_GLOW_STOPS,
  COLORS,
  REDUCED_MOTION,
} from '../../theme/tokens';

/**
 * The editor's one visual language for "Lumio is writing this".
 *
 * Every generation in this page wears the same two marks — a coloured arc
 * travelling around the edge of whatever is being written, and drifting,
 * heavily out-of-focus colour behind it. The arc is the blog list's own
 * generating row, so an owner who has watched an article being written in the
 * list recognises it on a single input.
 *
 * Kept here rather than in each `.styles.ts` because there are five hosts (the
 * whole page, the body editor, an SEO field, a list row, the hero thumbnail) and
 * five copies of a gradient is how they drift apart.
 */

/** The three-stop brand sweep, as a border paint for a Generate control. */
export const GENERATE_BORDER = `linear-gradient(120deg, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]}, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]}, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]})`; // design-allow

/**
 * A gradient BORDER on a transparent centre: two background images, the inner
 * one clipped to the padding box and the outer one to the border box, so the
 * ring is painted without a wrapper element.
 *
 * The inner layer is a solid fill written as a gradient because `background-clip`
 * takes a list of IMAGES — a plain colour cannot occupy a slot in it. The host's
 * own surface arrives through `--gen-surface`, which is also what a hover state
 * changes: one place paints the ring, and everything else just swaps the colour
 * under it.
 */
export const gradientRing = (surface: string) => css`
  --gen-surface: ${surface};
  border: 1px solid transparent;
  background-image: linear-gradient(var(--gen-surface), var(--gen-surface)), ${GENERATE_BORDER}; // design-allow
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

/** One loop of the arc travelling around an edge. */
export const glowSpin = keyframes`
  to {
    --blog-write-glow-angle: 360deg;
  }
`;

/** The blobs drift and swell; nothing about the path repeats on a short cycle. */
export const blobDrift = keyframes`
  0% {
    transform: translate3d(-8%, -6%, 0) scale(1.1);
  }
  33% {
    transform: translate3d(10%, 8%, 0) scale(1.35);
  }
  66% {
    transform: translate3d(-6%, 10%, 0) scale(1.15);
  }
  100% {
    transform: translate3d(-8%, -6%, 0) scale(1.1);
  }
`;

// Same construction and stops as the blog list's generating row (see
// `pages/blog/components/GeneratingArticle/GeneratingArticle.styles.ts`): an
// animated custom property cannot be a static GRADIENTS token.
export const GLOW_RING = `conic-gradient(from var(--blog-write-glow-angle), transparent 0 ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.62}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.36}%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]} ${100 - BRAND_DEPLOYMENT_GLOW.ARC_PCT * 0.09}%, transparent 100%)`; // design-allow

// Mask-composite alpha idiom that cuts the gradient down to a ring; the `#000`
// is a structural alpha source, not a colour.
export const RING_MASK = 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)'; // design-allow

// Three soft colour fields, one per brand stop, thrown badly out of focus.
export const BLOBS = `radial-gradient(38% 60% at 22% 34%, ${BRAND_DEPLOYMENT_GLOW_STOPS[0]}, transparent 70%), radial-gradient(42% 66% at 74% 26%, ${BRAND_DEPLOYMENT_GLOW_STOPS[1]}, transparent 72%), radial-gradient(46% 62% at 52% 82%, ${BRAND_DEPLOYMENT_GLOW_STOPS[2]}, transparent 74%)`; // design-allow

/** The travelling arc, as an overlay filling its host. */
export const ringLayer = css`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.5px;
  background: ${GLOW_RING};
  -webkit-mask: ${RING_MASK};
  mask: ${RING_MASK};
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  filter: blur(${BRAND_DEPLOYMENT_GLOW.BLUR_PX}px);
  opacity: ${BRAND_DEPLOYMENT_GLOW.ALPHA};
  pointer-events: none;
  animation: ${glowSpin} ${BRAND_DEPLOYMENT_GLOW.SPIN_MS}ms linear infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/**
 * The drifting colour, as an overlay filling its host. `$strength` scales the
 * blur radius with the host: 26px reads as a wash on a text field and as fog on
 * a full page.
 */
export const blobLayer = (blur: number, opacity: number) => css`
  position: absolute;
  inset: -14px;
  border-radius: inherit;
  background: ${BLOBS};
  filter: blur(${blur}px);
  opacity: ${opacity};
  pointer-events: none;
  animation: ${blobDrift} ${BLOG_WRITE_ANIM.driftMs}ms ease-in-out infinite;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** A veil over the host's own content, so the wash reads as "being replaced". */
export const veilLayer = css`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: ${COLORS.pageBg};
  opacity: 0.35;
  pointer-events: none;
`;
